import { FormControl, FormHelperText, FormLabel, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { PInputState } from '@/components/atoms/inputState';
import { PMultiCheckbox } from '@/components/atoms/MultiCheckbox/PMultiCheckbox';

type RHFMultiCheckboxProps<T extends string | number> = {
  name: string;
  options: { label: string; value: T }[];
  row?: boolean;
  label?: string;
  spacing?: number;
  helperText?: ReactNode;
  onChange: (values: T[]) => void;
  sx: SxProps<Theme>;
  state?: PInputState;
};

/**
 * Minimal@4 component.
 */
export default function RHFMultiCheckbox<T extends string | number>({
  row,
  name,
  label,
  options,
  spacing,
  helperText,
  onChange,
  state,
  sx,
}: RHFMultiCheckboxProps<T>) {
  const { control } = useFormContext();

  const getSelected = (selectedItems: T[], item: T) =>
    selectedItems.includes(item) ? selectedItems.filter((value) => value !== item) : [...selectedItems, item];

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field, fieldState: { error } }) => (
        <FormControl component="fieldset">
          {label ? (
            <FormLabel component="legend" sx={{ typography: 'body2' }}>
              {label}
            </FormLabel>
          ) : null}

          <PMultiCheckbox
            options={options}
            value={field.value ?? []}
            onToggle={(optionValue) => onChange(getSelected(field.value, optionValue))}
            row={row}
            spacing={spacing}
            itemSx={sx}
            state={state}
          />

          {!!error || helperText ? (
            <FormHelperText error={!!error} sx={{ mx: 0 }}>
              {error ? error?.message : helperText}
            </FormHelperText>
          ) : null}
        </FormControl>
      )}
    />
  );
}
